import {MipLevelRegion, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {HalImage} from "@cm/hal"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {definition} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/penalize-by-distance/definition"
import {Vector2} from "@cm/math"
import {HalPainterImageCompositorRef} from "@cm/hal/hal-ref"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {AnyImageOpHalData, CtorArgs, ImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {UPDATE_COMPLEXITY_MID} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

type Definition = typeof definition

export class ImageOpTilingPenalizeByDistanceHal extends ImageOpHal<Definition, ImageOpTilingPenalizeByDistanceHalData> {
    constructor(ctx: ImageOpContextHal, parameters: ImageOpHalParameters<Definition>) {
        super(ctx)
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpTilingPenalizeByDistanceHalData>): ImageOpTilingPenalizeByDistanceHalData {
        return new ImageOpTilingPenalizeByDistanceHalData({
            ...args,
            descriptor: args.parameters.correlation.descriptor,
            outputImageReuse: "reuse-if-same-descriptor",
            updateComplexity: UPDATE_COMPLEXITY_MID,
        })
    }
}

class ImageOpTilingPenalizeByDistanceHalData extends ImageOpHalData<Definition> {
    constructor(args: CtorArgs<Definition, ImageOpTilingPenalizeByDistanceHalData>) {
        super(args)
        this.compositor =
            args.prevData?.compositor.addRef() ??
            args.ctx.createPainter(
                "compositor",
                `
            uniform vec2 u_referencePosition;
            uniform vec2 u_offset;
            uniform ivec2 u_size;
            uniform int u_level;
            uniform vec2 u_penaltyPerPixel;
            uniform vec2 u_penaltyDirectionX;
            uniform vec2 u_penaltyDirectionY;
            
            float computePenalty(vec2 position) {
                if (position.x < 0.0 || position.y < 0.0 || position.x >= float(u_size.x) || position.y >= float(u_size.y)) {
                    return 10000.0;    // kinda infinitely penalize out-of-bounds pixels
                }
                vec2 delta = position - u_referencePosition;
                float penaltyX = dot(delta, u_penaltyDirectionX) * u_penaltyPerPixel.x;
                float penaltyY = dot(delta, u_penaltyDirectionY) * u_penaltyPerPixel.y;
                // apply penalty in an elliptical shape
                return sqrt(penaltyX * penaltyX + penaltyY * penaltyY);
            }
            
            vec4 computeColor(ivec2 targetPixel) {
                float correlation = texelFetch0(targetPixel).r;
                vec2 offset = u_offset; // + texelFetch1(ivec2(0, 0)).xy;
                vec2 position = (offset + vec2(targetPixel) + 0.5) * float(1 << u_level);
                float penalty = computePenalty(position);
                return vec4(correlation - penalty, 0, 0, 1);
            }
        `,
            )
    }

    protected override dispose() {
        this.compositor.release()
        super.dispose()
    }

    protected override async writeRegion(outputImage: HalImage, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void> {
        const {correlation, referencePosition, offset, imageSize, level, penaltyPerPixel, penaltyDirectionX: penaltyDirectionXIn} = this.parameters
        // update source image
        const correlationImage = await correlation.getUpdatedHalImage(
            {
                x: 0,
                y: 0,
                width: correlation.descriptor.width,
                height: correlation.descriptor.height,
                mipLevel: 0,
            },
            options,
        )
        // const offsetImage = await offsetImage.getUpdatedHalImage(
        //     {
        //         x: 0,
        //         y: 0,
        //         width: offsetImage.descriptor.width,
        //         height: offsetImage.descriptor.height,
        //         mipLevel: 0,
        //     },
        //     options,
        // )

        const penaltyDirectionX = Vector2.normalize(penaltyDirectionXIn ?? new Vector2(1, 0))
        const penaltyDirectionY = Vector2.perp(penaltyDirectionX)
        this.compositor.ref.paint({
            target: outputImage,
            sourceImages: [correlationImage.halImage], //offsetImage.halImage],
            parameters: {
                u_referencePosition: {type: "float2", value: referencePosition},
                u_offset: {type: "float2", value: offset},
                u_size: {type: "int2", value: {x: imageSize.width, y: imageSize.height}},
                u_level: {type: "int", value: level},
                u_penaltyPerPixel: {type: "float2", value: penaltyPerPixel},
                u_penaltyDirectionX: {type: "float2", value: penaltyDirectionX},
                u_penaltyDirectionY: {type: "float2", value: penaltyDirectionY},
            },
            options: {
                targetRegion: mipLevelRegion,
                targetMipLevel: mipLevelRegion.mipLevel,
            },
        })

        correlationImage.release()
        // offsetImage.release()
    }

    protected override onSourceRegionsInvalidated(_sourceImage: AnyImageOpHalData, _sourceRegions: Regions) {
        this.invalidateRegions()
    }

    private compositor: HalPainterImageCompositorRef
}
